import { InputField, SelectCheckbox } from '@advanza/input'
import { Button, PreIcon } from '@advanza/ui'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import { findGeoIdsByRange } from 'actions/subscriptions'
import { useToast } from 'components/misc/Toaster'
import { useSubscriptionArea } from 'hooks/providerHooks'
import { useMemo, useState } from 'react'

interface Props {
    subscriptionId: string
    prefillMunicipalityId?: number
}

const collator = new Intl.Collator(undefined, { sensitivity: 'base' })

const LeadEstimatorRadius = ({ subscriptionId, prefillMunicipalityId }: Props) => {
    const {
        subscription,
        municipalities,
        municipalitiesGrouped,
        replaceMunicipalities,
        setCenter,
    } = useSubscriptionArea(subscriptionId, true)
    const { addToast } = useToast()

    const [distance, setDistance] = useState('')
    const [municipalityId, setMunicipalityId] = useState<number | undefined>(prefillMunicipalityId)
    const [isApplying, setIsApplying] = useState(false)

    const countryCode = subscription.country_code
    const regions = municipalitiesGrouped?.[countryCode] || {}

    // Naming and sorting hundreds of municipalities on every km keystroke repeats unchanged work.
    const options = useMemo(
        () =>
            Object.entries(regions)
                .flatMap(([regionName, ids]) =>
                    ids
                        .filter((id) => municipalities?.[id])
                        .map((id) => ({
                            value: id,
                            name: `${municipalities[id].name} (${regionName})`,
                        })),
                )
                .sort((a, b) => collator.compare(a.name, b.name)),
        [regions, municipalities],
    )

    const applyRadius = (km = distance, id = municipalityId) => {
        const municipality = id ? municipalities?.[id] : undefined
        const range = parseInt(km, 10)

        const isValidRange = range > 0
        if (!municipality || !isValidRange || isApplying) {
            return
        }
        setIsApplying(true)
        findGeoIdsByRange(municipality.latitude, municipality.longitude, range, countryCode)
            .then((ids) => {
                setCenter(municipality)
                replaceMunicipalities(ids)
            })
            .catch(() => {
                addToast({ msg: 'error while fetching data' })
            })
            .finally(() => setIsApplying(false))
    }

    const canApply = Boolean(municipalityId) && parseInt(distance, 10) > 0

    return (
        <div>
            <div className="flex items-center gap-2">
                <InputField
                    type="number"
                    inputStyle={{ maxWidth: 75 }}
                    after="km"
                    value={distance}
                    onChange={({ target }) => setDistance(target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applyRadius()}
                />
                <div className="grow">
                    <SelectCheckbox
                        wide
                        stickyHeaderAndSearch
                        multiple={false}
                        placeholder="Municipality"
                        options={options}
                        value={municipalityId}
                        onChangeValue={([id]) => {
                            setMunicipalityId(id)
                            applyRadius(distance, id)
                        }}
                    />
                </div>
                <Button
                    name="text"
                    disabled={!canApply || isApplying}
                    onClick={() => applyRadius()}>
                    {isApplying ? (
                        <LoadingDots />
                    ) : (
                        <PreIcon primColor icon="refresh">
                            Update
                        </PreIcon>
                    )}
                </Button>
            </div>
            <p className="mt-2 text-md text-neutral-600">
                Municipalities are fully selected once part of their area falls within the radius,
                so the resulting work area may be larger than the distance entered.
            </p>
        </div>
    )
}

export default LeadEstimatorRadius
